import { DAY_SECONDS, formatClock, type AuditEntry } from '@hotlist/shared';

/**
 * Your own searches, read back to you.
 *
 * DESIGN.md section 7 calls M4 the milestone that proves the thesis, and this
 * panel is where the proving happens or does not. The design constraint that
 * matters is negative: **nothing in this panel accuses anybody.**
 *
 * There is no score, no flag, no red "unjustified" badge, no threshold that
 * trips. What there is, is a list of the things you looked up and what you
 * wrote down about why, which for most players will be a column of blanks. The
 * blanks are the argument. A game that graded them would be telling the player
 * what to think about a record that, in the real system, nobody grades at all.
 *
 * It is also opt-in, and the button says `read` rather than `audit`. You have
 * to decide to look. That is the finding: the log was complete and available
 * the whole time and the only thing that ever stood between anybody and it was
 * that nothing suggested opening it.
 */

export class AuditPanel {
  private on = false;
  private readonly tally: HTMLElement;
  private readonly entries: HTMLElement;
  private readonly button: HTMLButtonElement;
  private daySeconds = DAY_SECONDS;
  private opened = false;

  constructor(onRead: () => void) {
    this.tally = must('audit-tally');
    this.entries = must('audit-entries');
    this.button = must('audit-refresh') as HTMLButtonElement;
    this.button.addEventListener('click', () => {
      this.opened = true;
      onRead();
    });
  }

  /**
   * Marks this panel live. Visibility belongs to the console, which owns the
   * tabs: two things deciding whether one element is on screen is how a panel
   * ends up hidden behind the tab that is supposed to show it.
   */
  enable(): void {
    this.on = true;
  }

  /** The county's day, so these timestamps line up with the query log's. */
  setDaySeconds(seconds: number): void {
    if (Number.isFinite(seconds) && seconds > 0) this.daySeconds = seconds;
  }

  /** Whether the player has ever chosen to look. Worth knowing; see main.ts. */
  get everOpened(): boolean {
    return this.opened;
  }

  show(entries: AuditEntry[], total: number, unexplained: number): void {
    this.tally.textContent =
      total === 0
        ? 'No searches.'
        : `${total} search${total === 1 ? '' : 'es'}, ${unexplained} with no reason given.`;

    this.entries.replaceChildren();
    if (entries.length === 0) return;

    for (const entry of entries) {
      const row = document.createElement('div');
      row.className = 'audit-row';

      const when = document.createElement('span');
      when.className = 'audit-when';
      when.textContent = formatClock(entry.at, this.daySeconds);

      const term = document.createElement('span');
      term.className = 'audit-term';
      term.textContent = entry.term;

      // The reason, in the player's own words, or the fact that there wasn't
      // one. Both are printed the same way, in the same color, because the
      // system treats them the same way. It ran both searches.
      const why = document.createElement('span');
      const stated = entry.reason.trim().length > 0;
      why.className = stated ? 'audit-why' : 'audit-why audit-why-blank';
      why.textContent = stated ? entry.reason : 'no reason given';

      const rows = document.createElement('span');
      rows.className = 'audit-rows';
      rows.textContent = `${entry.rows}`;

      row.append(when, term, why, rows);
      this.entries.append(row);
    }
  }

  fail(message: string): void {
    this.tally.textContent = message;
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
